/**
 * Level format v1/v2 ("skypulse-level@1" / "@2") — see README format guide.
 * This format is the contract a future community editor will target.
 * v2 adds: `physics.gravity`, `minThrottle`, glyphs `U` (tunnel floor hole)
 * and `I` (ice); ramps (`/`, `\`) become fully live.
 */
import type { GravityPreset } from "../config/tuning";

export type CellKind =
  | "empty"
  | "floor"
  | "block"
  | "tunnel"
  | "tunnelHole" // U: roof present, floor absent (R2 §2.2)
  | "ice" // I: drift surface (R2 Feature C)
  | "rampUp"
  | "rampDown"
  | "boost"
  | "sticky"
  | "hazard"
  | "bit"
  | "checkpoint"
  | "finish";

export interface Cell {
  kind: CellKind;
  /** Block height in levels (1–3) for kind "block"; 0 otherwise. */
  height: number;
  /** For kind "bit": index 0..2 of this bit within the level. */
  bitIndex: number;
  /** Ramps: surface height at the ENTERING edge (resolved at parse time).
   *  Surface(frac) = rampBase + rampDir·frac across the row. */
  rampBase?: number;
  rampDir?: 1 | -1;
}

export interface MedalTimes {
  bronze: number;
  silver: number;
  gold: number;
  author: number;
}

/** Timed on-screen prompt (tutorial): shown while the ship is near `row`. */
export interface LevelPrompt {
  row: number;
  text: string;
}

export interface LevelFile {
  format: "skypulse-level@1" | "skypulse-level@2";
  id: string;
  name: string;
  songId: string;
  world: number;
  medalTimesMs?: MedalTimes;
  palette?: string;
  rows: string[];
  prompts?: LevelPrompt[];
  /** v2: gravity preset (absent = "normal"; v1 files stay valid). */
  physics?: { gravity: GravityPreset };
  /** v2: minimum throttle the level's hazards are validated against. */
  minThrottle?: number;
}

export interface BitLocation {
  row: number;
  lane: number;
  index: number;
}

export interface ParsedLevel {
  id: string;
  name: string;
  songId: string;
  world: number;
  /** cells[row][lane]; row 0 is the start of the track. */
  cells: Cell[][];
  numRows: number;
  bits: BitLocation[];
  checkpointRows: number[];
  /** First all-finish row. */
  finishRow: number;
  medalTimesMs?: MedalTimes;
  palette: string;
  prompts: LevelPrompt[];
  gravity: GravityPreset;
  minThrottle: number;
}

/** Row-string glyphs (Appendix A). */
export const GLYPHS: Record<string, { kind: CellKind; height?: number }> = {
  ".": { kind: "empty" },
  _: { kind: "floor" },
  "1": { kind: "block", height: 1 },
  "2": { kind: "block", height: 2 },
  "3": { kind: "block", height: 3 },
  T: { kind: "tunnel" },
  U: { kind: "tunnelHole" },
  I: { kind: "ice" },
  "/": { kind: "rampUp" },
  "\\": { kind: "rampDown" },
  B: { kind: "boost" },
  S: { kind: "sticky" },
  X: { kind: "hazard" },
  o: { kind: "bit" },
  C: { kind: "checkpoint" },
  F: { kind: "finish" },
};
